import React, { useEffect, useState } from "react";
// @ts-ignore: side-effect SCSS import without type declarations
import "./sidepanel.scss";
import { Link, useLocation } from "react-router-dom";
import { useSidenavStore } from "../../store/store";
import { dashboardNavs } from "../../navigationSidepanel";
import { hasModuleAccess } from "../../hooks/permissionUtils";
import { RolePermissions, rolesObjType } from "../../types/api";
import { useAuth } from "../../auth/AuthProvider";
import { usePermissions } from "../../hooks/usePermission";

interface NavItem {
  title: string;
  path?: string;
  subModuleKey?: string;
  children?: NavItem[];
  matchPaths?: string[];
  note?: string;
}

const navGroupMapping: Record<string, keyof typeof dashboardNavs> = {
  "fire-system": "fire-system",
  tasks: "fire-system",
  "policies-manuals": "fire-system",
  folder: "fire-system",
  "risk-assessment": "fire-system",
  budgeting: "fire-system",
  "create-budgeting": "fire-system",
  trainings: "fire-system",
  "fire-drill": "fire-system",
  "safety-equipment": "fire-system",
  "fires-safety-audits": "fire-system",
  "fire-safety-audits": "fire-system",
  "fire-safety-incident": "fire-system",
  "fire-incident-investigation": "fire-system",
  // ---------------//
  "fire-safety-incident-admin": "fire-safety-incident-admin",
  "incident-dashboard": "fire-safety-incident-admin",
  incidents: "fire-safety-incident-admin",
  report: "fire-safety-incident-admin",
  // ------master-dashboard-------//
  regions: "master-dashboard",
  premises: "master-dashboard",
  "add-premises": "master-dashboard",
  "update-premises": "master-dashboard",
  "view-premises": "master-dashboard",
  departments: "master-dashboard",
  "role-management": "master-dashboard",
  "user-management": "master-dashboard",
  "budget-categories": "master-dashboard",
  "washroom-checklist-category": "master-dashboard",
  "washroom-types-list": "master-dashboard",
  "firerisk-processes": "master-dashboard",
  "firerisk-sub-processes": "master-dashboard",
  "policy-tags": "master-dashboard",
  suppliers: "master-dashboard",
  "legal-contract-types": "master-dashboard",
  "assets-equipment": "master-dashboard",
  equipment: "master-dashboard",
  "equipment-category": "master-dashboard",
  "equipment-subcategory": "master-dashboard",
  "emergency-category": "master-dashboard",
  "emergency-subcategory": "master-dashboard",
  "asset-equipment-category": "master-dashboard",
  "asset-equipment-subcategory": "master-dashboard",
  "manufacturers-list": "master-dashboard",
  "vendor-list": "master-dashboard",
  "location-master": "master-dashboard",
  "maintenancetype-list": "master-dashboard",
  "unit-of-measure-list": "master-dashboard",
  "incident-severity-levels": "master-dashboard",
  "hazard-material-category": "master-dashboard",
  "disposal-methods-list": "master-dashboard",
  "compliance-types": "master-dashboard",
  "utility-asset-type-list": "master-dashboard",
  "round-types": "master-dashboard",
  "master-data/equipment-management": "master-dashboard",
  // ---------------//
  "washroom-management": "washroom-management",
  "washroom-management/dashboard": "washroom-management",
  "washroom-maintenance": "washroom-management",
  "washroom-checklists": "washroom-management",
  "washroom-audits": "washroom-management",

  "washroom-management/audits/view": "washroom-management",
  "laws-regulations-policies": "laws-regulations",
  "laws-regulations": "laws-regulations",
  "laws-regulations-audit": "laws-regulations",
  "laws-regulations-tasks": "laws-regulations",
  "facility-information": "laws-regulations",
  "premises-information-view": "laws-regulations",
  "law-folder": "laws-regulations",
  "staff-management": "laws-regulations",
  // ---------------//
  "equipment-management": "equipment-management",
  "equipment-management/equipmentTasks": "equipment-management",
  "equipment-inventory-registration": "equipment-management",
  "maintenance-scheduling-and-tracking": "equipment-management",
  "documentation-and-history": "equipment-management",
  "equipment-management/staff-training-evaluation": "equipment-management",
  "equipment-management/staff-training-evaluation/dashboard":
    "equipment-management",
  "equipment-management/staff-training-evaluation/upcoming-sessions":
    "equipment-management",
  "equipment-management/staff-training-evaluation/past-evaluations":
    "equipment-management",
  "report-and-analytics": "equipment-management",
  "equipment-management/incident": "equipment-management",
  "equipment-management/rfid-demo-console": "equipment-management",
  // -----------------//
  "hazardous-materials": "hazardous-materials",
  "hazardous-materials/inventory": "hazardous-materials",
  "hazardous-materials/safety-compliance-info": "hazardous-materials",
  "hazardous-materials/storage-records": "hazardous-materials",
  "hazardous-materials/tracking-logs": "hazardous-materials",
  "hazardous-materials/spill-reporting": "hazardous-materials",
  "hazardous-materials/inspection-checks": "hazardous-materials",
  "hazardous-materials/disposal-management": "hazardous-materials",
  "hazardous-materials/search-reporting": "hazardous-materials",
  "hazardous-materials/waste-tracking": "hazardous-materials",
  "hazardous-materials/policies-manuals": "hazardous-materials",
  "hazardous-materials/policies-manuals/folder": "hazardous-materials",
  "hazardous-materials/staff-training-evaluation": "hazardous-materials",
  "hazardous-materials/staff-training-evaluation/dashboard":
    "hazardous-materials",
  "hazardous-materials/staff-training-evaluation/upcoming-sessions":
    "hazardous-materials",
  "hazardous-materials/staff-training-evaluation/past-evaluations":
    "hazardous-materials",
  "hazardous-materials/testing-drills": "hazardous-materials",
  "safety-equipment/fire-extinguisher": "fire-system",
  "safety-equipment/fire-door": "fire-system",
  "safety-equipment/smoke-detector": "fire-system",
  "safety-equipment/heat-detector": "fire-system",
  "safety-equipment/sprinkler": "fire-system",
  "safety-equipment/control-panel": "fire-system",
  "safety-equipment/hose": "fire-system",
  // External Emergency Preparedness
  "external-emergency-preparedness": "external-emergency-preparedness-module",
  "external-emergency-preparedness/dashboard":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/testing-drills":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/risk-assessment":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/external-agencies-coordination":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/notifications":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/send-notification":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/erp-executions":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/trigger-erp":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/monitoring":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/resource-for-readiness":
    "external-emergency-preparedness-module",
  "external-emergency-preparedness/incidents":
    "external-emergency-preparedness-module",

  // Utility Systems
  "utility-systems": "utility-systems-module",
  "utility-systems/dashboard": "utility-systems-module",
  "utility-systems/maintenance": "utility-systems-module",
  "utility-systems/maintenance/dashboard": "utility-systems-module",
  "utility-systems/maintenance/schedules": "utility-systems-module",
  "utility-systems/maintenance/logs": "utility-systems-module",
  "utility-systems/asset-inventory-tracking": "utility-systems-module",
  "utility-systems/compliance-safety": "utility-systems-module",
  "utility-systems/space-management": "utility-systems-module",
  // Safety & Facility Rounds
  "safety-facility-rounds": "safety-facility-rounds-module",
  "safety-facility-rounds/dashboard": "safety-facility-rounds-module",
  // Security
  security: "security-module",
  "security/dashboard": "security-module",
};

const Sidepanel = () => {
  const { isNav } = useSidenavStore();
  const { user } = useAuth();
  const location = useLocation();
  // const { user, roleData, userType } = useAuth(); // Get user, roleData, and userType from AuthContext
  const [currentNav, setCurrentNav] = useState<NavItem[]>(
    dashboardNavs.default,
  );
  const [navGroupKey, setNavGroupKey] = useState<string>("default");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const { canRead } = usePermissions();

  const isAdmin = user?.roles.includes("orgadmin");
  //  || user?.roles.includes("superadmin");

  // Determine the user roles and roleName
  // const userRoles = user?.roles || []; // e.g., ["orguser"]
  // Extract roleName from roleData for orguser
  // const roleName = userType === "orguser" && roleData?.roleName ? roleData.roleName : "unknown";

  // Determine the effective role for navigation
  // const effectiveRole = userRoles.includes("superadmin")
  //   ? "superadmin"
  //   : userRoles.includes("orgadmin")
  //   ? "orgadmin"
  //   : userRoles.includes("orguser") && roleName === "trainer"
  //   ? "trainer"
  //   : "trainee"; // Default for other orgusers

  useEffect(() => {
    const pathKey = location.pathname.split("/")[1];
    const dashboardKey = navGroupMapping[pathKey] || "default";
    setCurrentNav(dashboardNavs[dashboardKey]);
    setNavGroupKey(dashboardKey);
  }, [location]);

  const hasAccess = (subModuleKey?: string): boolean => {
    return isAdmin || (subModuleKey ? canRead(subModuleKey) : false);
  };

  const hasAccessRecursive = (item: NavItem): boolean => {
    if (isAdmin) return true;
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => hasAccessRecursive(child));
    }
    return hasAccess(item.subModuleKey);
  };

  // Filter children based on user role
  // const filterChildrenByRole = (children: NavItem[]): NavItem[] => {
  //   if (["superadmin", "orgadmin", "trainer"].includes(effectiveRole)) {
  //     // Show TrainingDashboard, UpcomingSessions, PastEvaluations
  //     return children.filter((child) =>
  //       ["Training Dashboard", "Upcoming Sessions", "Past Evaluations"].includes(
  //         child.title
  //       )
  //     );
  //   }
  // For other orgusers (excluding trainer), show only TrainingDashboard and PastEvaluations
  //   return children.filter(
  //     (child) =>
  //       child.title === "Training Dashboard" || child.title === "Past Evaluations"
  //   );
  // };
  const normalizePath = (path: string) => path.replace(/\/+$/, "");
  const isPathActive = (item: NavItem) => {
    const currentPath = normalizePath(location.pathname);
    if (item.matchPaths && item.matchPaths.length > 0) {
      return item.matchPaths.some((matchPath) => {
        const normalizedMatchPath = normalizePath(matchPath);
        if (currentPath === normalizedMatchPath) {
          return true;
        }
        if (
          currentPath.startsWith(normalizedMatchPath + "/") &&
          currentPath.substring(normalizedMatchPath.length + 1).split("/")
            .length >= 1
        ) {
          return true;
        }
        return false;
      });
    }

    return location.pathname === item.path;
  };

  const findActiveTrail = (
    items: NavItem[],
    parentKey = "",
  ): string[] | null => {
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      if (isPathActive(item)) {
        return [key];
      }
      if (item.children && item.children.length > 0) {
        const childTrail = findActiveTrail(item.children, key);
        if (childTrail) {
          return [key, ...childTrail];
        }
      }
    }
    return null;
  };

  const getAllExpandableKeys = (
    items: NavItem[],
    parentKey = "",
  ): string[] => {
    const keys: string[] = [];
    items.forEach((item, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      if (item.children && item.children.length > 0) {
        keys.push(key, ...getAllExpandableKeys(item.children, key));
      }
    });
    return keys;
  };

  useEffect(() => {
    if (navGroupKey === "fire-system") {
      // Fire Systems menu: all accordions/main menus stay open by default
      setExpandedKeys(new Set(getAllExpandableKeys(currentNav)));
      return;
    }
    const trail = findActiveTrail(currentNav);
    setExpandedKeys(new Set(trail ?? []));
  }, [location.pathname, currentNav, navGroupKey]);

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderNavItems = (
    items: NavItem[],
    parentKey = "",
  ): React.ReactNode =>
    items.map((item, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      const hasChildren = item.children && item.children.length > 0;
      const itemHasAccess = hasAccessRecursive(item);

      if (!itemHasAccess && currentNav[0]?.title !== "Regions") {
        //Need to change this temp fix
        return null;
      }

      const isActive = !hasChildren && isPathActive(item);
      const isExpanded = expandedKeys.has(key);

      return (
        <li
          key={key}
          className={`nav-item dropdown ${isActive ? "active" : ""} ${
            isExpanded ? "open" : ""
          }`}
        >
          {hasChildren ? (
            <div
              onClick={() => toggleExpand(key)}
              className="dropdown-toggle cursor-pointer"
            >
              <div className="toggle-row">
                <span className="title">{item.title}</span>
              </div>
              {/* {item.note && <span className="note">{item.note}</span>} */}
            </div>
          ) : (
            <Link to={item.path!}>
              <span className="title">{item.title}</span>
            </Link>
          )}

          {hasChildren && isExpanded && (
            <ul className="dropdown-menu">
              {renderNavItems(item.children!, key)}
            </ul>
          )}
        </li>
      );
    });

  return (
    <>
      {isNav && (
        <div className="side-nav">
          <div className="side-nav-inner">
            <ul className="side-nav-menu scrollable">
              {renderNavItems(currentNav)}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidepanel;
